import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000' });

API.interceptors.request.use(config => {
  const token = localStorage.getItem('cms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];

export default function Dashboard({ user, onOpenProject, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'newProject' | 'invite' | 'members'
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await API.get('/api/projects');
      setProjects(res.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const res = await API.post('/api/projects', { name: newProjectName.trim() });
      setProjects(p => [...p, res.data]);
      setNewProjectName('');
      setModal(null);
    } catch(e) {
      setError(e.response?.data?.error || 'Error creating project');
    }
  };

  // const handleDeleteProject = async (id) => {
  //   if (!window.confirm('Delete this project and all its data?')) return;
  //   try {
  //     await API.delete(`/api/projects/${id}`);
  //     setProjects(p => p.filter(pr => pr._id !== id));
  //   } catch(e) { alert('Error deleting project'); }
  // };

  const handleGenerateInvite = async () => {
    if (!selectedProject) return;
    try {
      const res = await API.post(`/api/projects/${selectedProject._id}/invite`, { role: inviteRole });
      setInviteLink(res.data.inviteLink);
      setInviteToken(res.data.token);
    } catch(e) {
      alert(e.response?.data?.error || 'Error generating invite');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRemoveMember = async (projectId, userId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await API.delete(`/api/projects/${projectId}/members/${userId}`);
      loadProjects();
    } catch(e) { alert('Error removing member'); }
  };

  const isOwner = (project) => project.owner?._id === user._id || project.owner === user._id;
  const getProjectRole = (project) => {
    if (isOwner(project)) return 'admin';
    const member = project.members?.find(m => m.user === user._id || m.user?._id === user._id);
    return member?.role || 'viewer';
  };

  return (
    <div className="dash-bg">
      <header className="dash-header">
        <div className="dash-brand">
          <div className="dash-brand-mark">CMS</div>
          <span className="dash-brand-name">SUNKIND CMS</span>
        </div>
        <div className="dash-user">
          <span className="dash-username">👤 {user.username}</span>
          <span className='dash-role-badge user'>member</span>
          <button className="dash-logout" onClick={onLogout}>Sign out</button>
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-top">
          <div>
            <h1 className="dash-title">Projects</h1>
            <p className="dash-sub">Select a project to start editing</p>
          </div>
          {/* <button className="dash-new-btn" onClick={() => { setModal('newProject'); setError(''); }}>
            + New Project
          </button> */}
        </div>

        {loading && <div className="dash-loading">Loading projects...</div>}

        {!loading && projects.length === 0 && (
          <div className="dash-empty">
            <div className="dash-empty-icon">⬡</div>
            <div className="dash-empty-text">No projects yet</div>
            <div className="dash-empty-sub">Create your first project or ask someone to invite you to theirs</div>
          </div>
        )}

        <div className="dash-grid">
          {projects.map(project => {
            const myRole = getProjectRole(project);
            return (
              <div key={project._id} className="dash-card" onClick={() => onOpenProject(project, myRole)}>
                <div className="dash-card-color" style={{background: project.color || '#6366f1'}} />
                <div className="dash-card-body">
                  <div className="dash-card-name">{project.name}</div>
                  <div className="dash-card-meta">
                    <span className={`dash-role-badge ${myRole}`}>{myRole}</span>
                    <span className="dash-card-members">
                      {(project.members?.length || 0) + 1} member{(project.members?.length || 0) + 1 !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {isOwner(project) && (
                  <div className="dash-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="dash-card-btn" onClick={() => { setSelectedProject(project); setInviteLink(''); setModal('invite'); }}>
                      Invite
                    </button>
                    <button className="dash-card-btn" onClick={() => { setSelectedProject(project); setModal('members'); }}>
                      Members
                    </button>
                    {/* <button className="dash-card-btn danger" onClick={() => handleDeleteProject(project._id)}>
                      Delete
                    </button> */}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* NEW PROJECT MODAL */}
      {modal === 'newProject' && (
        <div className="dash-overlay" onClick={() => setModal(null)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-title">New Project</div>
            {error && <div className="dash-modal-error">{error}</div>}
            <label className="dash-modal-label">Project Name</label>
            <input className="dash-modal-input" value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
              placeholder="e.g. My Website, Client Project..." autoFocus />
            <div className="dash-modal-actions">
              <button className="dash-modal-cancel" onClick={() => setModal(null)}>Cancel</button>
              <button className="dash-modal-confirm" onClick={handleCreateProject}>Create Project</button>
            </div>
          </div>
        </div>
      )}

      {/* INVITE MODAL */}
      {modal === 'invite' && selectedProject && (
        <div className="dash-overlay" onClick={() => { setModal(null); setInviteLink(''); }}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-title">Invite to {selectedProject.name}</div>
            <label className="dash-modal-label">Role</label>
            <div className="dash-role-select">
              <div className={`dash-role-option ${inviteRole === 'editor' ? 'sel' : ''}`} onClick={() => setInviteRole('editor')}>
                <div className="dash-role-option-name">Editor</div>
                <div className="dash-role-option-desc">Can view and edit all content</div>
              </div>
              <div className={`dash-role-option ${inviteRole === 'viewer' ? 'sel' : ''}`} onClick={() => setInviteRole('viewer')}>
                <div className="dash-role-option-name">Viewer</div>
                <div className="dash-role-option-desc">Can only view content, cannot edit</div>
              </div>
            </div>
            <button className="dash-modal-confirm" style={{width:'100%',marginTop:16}} onClick={handleGenerateInvite}>
              Generate Invite Link
            </button>
            {inviteLink && (
              <div className="dash-invite-result">
                <div className="dash-invite-label">Share this link with your team member:</div>
                <div className="dash-invite-link-box">
                  <span className="dash-invite-link">{inviteLink}</span>
                  <button className="dash-copy-btn" onClick={handleCopy}>{copied ? '✓ Copied!' : 'Copy'}</button>
                </div>
                <div className="dash-invite-note">⚠ This link expires in 7 days and can only be used once.</div>
              </div>
            )}
            <div className="dash-modal-actions" style={{marginTop:16}}>
              <button className="dash-modal-cancel" onClick={() => { setModal(null); setInviteLink(''); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* MEMBERS MODAL */}
      {modal === 'members' && selectedProject && (
        <div className="dash-overlay" onClick={() => setModal(null)}>
          <div className="dash-modal" onClick={e => e.stopPropagation()}>
            <div className="dash-modal-title">Members — {selectedProject.name}</div>
            <div className="dash-members-list">
              <div className="dash-member-item">
                <span className="dash-member-name">{selectedProject.owner?.username || 'Owner'}</span>
                <span className="dash-role-badge admin">admin</span>
              </div>
              {(selectedProject.members || []).map((m, i) => (
                <div key={i} className="dash-member-item">
                  <span className="dash-member-name">{m.username || m.user?.username || 'Member'}</span>
                  <span className={`dash-role-badge ${m.role}`}>{m.role}</span>
                  <button className="dash-remove-member" onClick={() => handleRemoveMember(selectedProject._id, m.user?._id || m.user)}>×</button>
                </div>
              ))}
              {(selectedProject.members || []).length === 0 && (
                <div style={{color:'#666',fontSize:13,padding:'10px 0'}}>No members yet. Generate an invite link to add people.</div>
              )}
            </div>
            <div className="dash-modal-actions">
              <button className="dash-modal-cancel" onClick={() => setModal(null)}>Close</button>
              <button className="dash-modal-confirm" onClick={() => { setModal('invite'); setInviteLink(''); }}>+ Invite Member</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
